import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { transferData } from "../../../redux/slices/transferSlice";
import { fetchUserOption } from "../../../redux/slices/clientSlice";
import { Users, ArrowRightLeft, Loader2, CheckCircle } from "lucide-react";
import MainLayout from "../MainLayout";
import LoadingIndicator from "../../utils/LoadingIndicator";

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '48px',
    borderRadius: '0.75rem',
    borderColor: state.isFocused ? '#4f46e5' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 2px #c7d2fe' : 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
    '&:hover': {
      borderColor: state.isFocused ? '#4f46e5' : '#9ca3af',
    },
    transition: 'all 0.15s ease-in-out',
    backgroundColor: '#f9fafb',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#1f2937',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#9ca3af',
  }),
};

const UserSelect = ({
  label,
  users,
  value,
  onChange,
  placeholder,
  disabledUser,
}) => {
  const sortedAndFilteredOptions = useMemo(() => {
    if (!users) return [];

    const sortedUsers = [...users].sort((a, b) => {
      const nameA = a.name ? a.name.toUpperCase() : '';
      const nameB = b.name ? b.name.toUpperCase() : '';
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });

    const filteredUsers = sortedUsers.filter(
      (user) => user.id !== disabledUser || user.id === value
    );

    return filteredUsers.map((user) => ({
      value: user.id,
      label: `${user.name} ${user.last_name} (${user.emp_code})`,
    }));
  }, [users, disabledUser, value]);

  const selectedOption = sortedAndFilteredOptions.find(
    (option) => option.value === value
  );

  const handleSelectChange = (option) => {
    onChange(option ? option.value : null);
  };

  return (
    <div className="flex flex-col space-y-1.5 w-full">
      <label className="text-sm font-semibold text-gray-700 flex items-center tracking-wide">
        <Users className="w-4 h-4 mr-2 text-indigo-600" />
        {label}
      </label>
      <Select
        options={sortedAndFilteredOptions}
        value={selectedOption}
        onChange={handleSelectChange}
        placeholder={placeholder}
        isClearable={true}
        isSearchable={true}
        isDisabled={!users || users.length === 0}
        styles={customStyles}
        classNamePrefix="react-select"
        name="user-select"
      />
    </div>
  );
};

const DataTransfer = () => {
  const dispatch = useDispatch();
  const { transferStatus } = useSelector((state) => state.transfer);
  const { userOption } = useSelector((state) => state.client);

  const [form, setForm] = useState({
    old_user_id: null,
    new_user_id: null,
  });

  useEffect(() => {
    dispatch(fetchUserOption());
  }, [dispatch]);

  useEffect(() => {
    if (transferStatus === "succeeded") {
      setForm({
        old_user_id: null,
        new_user_id: null,
      });
    }
  }, [transferStatus, dispatch]);

  const isReady = useMemo(
    () =>
      form.new_user_id &&
      form.old_user_id &&
      form.new_user_id !== form.old_user_id &&
      transferStatus !== "loading",
    [form.new_user_id, form.old_user_id, transferStatus]
  );

  const isLoading = transferStatus === "loading";
  const isSuccess = transferStatus === "succeeded";

  const handleFormChange = (key, value) => {
    setForm((prevForm) => ({ ...prevForm, [key]: value }));
  };

  const onSubmit = () => {
    if (isReady) {
      dispatch(transferData(form));
    }
  };

  return (
    <MainLayout title={'Data Transfer'}>
      {transferStatus === "pending" && <LoadingIndicator show />}

      <div className="p-4 sm:p-8 flex justify-center items-center h-full min-h-[90vh]">
        {" "}
        <div className="w-full max-w-[60vw] bg-white p-8 md:p-10 rounded-2xl shadow-2xl border border-gray-100">
          <div className="flex items-center border-b border-gray-200 pb-4 mb-6">
            <ArrowRightLeft className="w-7 h-7 text-indigo-600 mr-3" />
            <h1 className="text-3xl font-extrabold text-gray-800 tracking-tight">
              Data Transfer
            </h1>
          </div>

          {isSuccess && (
            <div className="bg-green-50 border-l-4 border-green-500 text-green-700 p-4 mb-6 rounded-md flex items-center">
              <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0" />
              <p className="font-medium">Data successfully transferred!</p>
            </div>
          )}

          <div className="flex flex-col md:flex-row gap-4 md:gap-6 mb-8 items-center">
            <div className="w-full md:w-5/12">
              <UserSelect
                label="Old User (FROM)"
                users={userOption}
                value={form.old_user_id}
                onChange={(id) => handleFormChange("old_user_id", id)}
                placeholder="Select the current user"
                disabledUser={form.new_user_id}
              />
            </div>

            <div className="md:w-2/12 flex justify-center py-2 md:py-0">
              <div className="bg-indigo-100 p-2 rounded-full border border-indigo-200">
                <ArrowRightLeft className="w-5 h-5 text-indigo-500 rotate-90 md:rotate-0" />
              </div>
            </div>

            <div className="w-full md:w-5/12">
              <UserSelect
                label="New User (TO)"
                users={userOption}
                value={form.new_user_id}
                onChange={(id) => handleFormChange("new_user_id", id)}
                placeholder="Select the new user"
                disabledUser={form.old_user_id}
              />
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end">
            <button
              onClick={onSubmit}
              disabled={!isReady}
              className={`
                px-8 py-3 text-lg font-bold rounded-xl shadow-lg transition duration-300 ease-in-out
                flex items-center justify-center min-w-[200px] tracking-wide
                ${
                  isReady
                    ? "bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 transform hover:scale-[1.02]"
                    : "bg-gray-200 text-gray-500 cursor-not-allowed shadow-inner"
                }
              `}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                  Processing Transfer...
                </>
              ) : (
                <>
                  <ArrowRightLeft className="w-5 h-5 mr-3" />
                  Initiate Data Transfer
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default DataTransfer;